import { AuthData } from "./../../types/LocalConfig";
import { ChatMessageType } from "@jd/jdee.im.sdk/lib/es/protocol/message/Type";
import { generateMessageId } from "@jd/jdee.im.sdk/lib/es/utils/utils";
import log from "@/utils/logger";
import ImService from "@/server/ImService";

import {
  CloudLinkStatusEnum,
  CloudLinkSessionStateEnum,
  changeCloudLinkStatus,
  getCloudLinkStatus
} from "./Connector";
import { FocusSDK, getClientType, getUser } from "@/utils";
import {
  cloudlinkRegister,
  addParticipants,
  createMeeting,
  conferenceCall,
  sendConferenceCardMsg
} from "@/api/meeting";
import bus from "@/utils/bus";
import { NPCRoom } from "./Invites/NPC";
import { message } from "antd";
import { getNoticeBot, sendNoticeCardMessage } from "@/api/chat";
import { isUndefined } from "lodash";
import i18n from "i18next";
import { Employee } from "@/types/chat";
import cache from "@/utils/cache";
import { MeetingPlatform } from "@/types/common";
import dayjs from "dayjs";
import { AVSDK, SessionStateEnum } from "@/baseComponents/Meeting/common";
import { getBigScreenMeetingTopic, handleMeetingSendMessage } from "@/baseComponents/Meeting/utils";

const logger = log.getLogger("cloudlink");

const isBigScreen = process.env.REACT_APP_X_FLAG === "olympic";
log.debug("isBigScreen", isBigScreen);
interface BasicUserInfo {
  pin: string;
  teamId: string;
  name: string;
  app: string;
  avatar: string;
}

export interface ToUserType {
  appId: string;
  userId: string;
  teamId: string;
  userName: string;
  avatar: string;
}

let currentMeetingInfo: {
  password: string;
  meetingNumber: string;
  conferenceUuid: string;
} | null = null;

export function getCurrentCloudLinkMeetingInfo() {
  return currentMeetingInfo;
}

export function setCurrentCloudLinkMeetingInfo(meetingInfo: any) {
  logger.debug("currentMeetingInfo", meetingInfo);
  currentMeetingInfo = meetingInfo;
}

export function createCloudLinkTerminalCall(
  terminals: string[],
  meetingTopic = getBigScreenMeetingTopic()
) {
  return createCloudLinkCall({
    terminals: {
      terminalId: terminals
    },
    opts: { meetingTopic }
  });
}

function invite({
  toUsers,
  terminals,
  meetingTopic
}: {
  toUsers?: ToUserType[];
  terminals?: {
    terminalId?: string[];
    room?: NPCRoom;
  };
  meetingTopic: string;
}) {
  if (!currentMeetingInfo) return;
  logger.debug("deeplink:--invite--", currentMeetingInfo, toUsers);
  const { password, meetingNumber, conferenceUuid } = currentMeetingInfo;

  if (toUsers && toUsers.length > 0) {
    handleMeetingSendMessage({
      avsdk: AVSDK.HUAWEI,
      meetingInfo: {
        meetingNumber,
        password,
        meetingTopic
      },
      sessionState: SessionStateEnum.Call,
      toUsers: toUsers.map((i) => ({
        pin: i.userId,
        teamId: i.teamId,
        app: i.appId,
        avatar: i.avatar,
        name: i.userName
      }))
    });
  }

  if (terminals && conferenceUuid) {
    if ((terminals.terminalId && terminals.terminalId.length > 0) || terminals.room) {
      addParticipants({
        conferenceUuid,
        terminalId: terminals.terminalId || [],
        room: terminals.room || null
      }).then(([_res, err]: any) => {
        if (err) {
          message.error(err);
        }
      });
    }
  }
}

export function createCloudLinkCall({
  toUsers,
  terminals,
  opts
}: {
  toUsers?: ToUserType[];
  terminals?: {
    terminalId?: string[];
    room?: NPCRoom;
  };
  opts: {
    meetingTopic: string;
    meetingPassword?: string;
    isvideooff?: boolean;
    isaudiooff?: boolean;
  };
}) {
  const { meetingTopic, meetingPassword = "", isvideooff, isaudiooff } = opts;
  return new Promise((resolve, reject) => {
    try {
      if (FocusSDK.getDeviceInfo().platform !== "win32") {
        reject(new Error("信创视频会议暂只支持windows"));
        return;
      }
      if (!terminals && (!toUsers || toUsers.length === 0)) {
        throw new Error("缺少必要参数");
      }
      logger.debug(`deeplink:--getCloudLinkStatus--`, getCloudLinkStatus());
      if (getCloudLinkStatus() === CloudLinkStatusEnum.IN_MEETING) {
        // 会议中，直接邀请
        logger.debug("会议中，直接邀请");
        invite({ toUsers, terminals, meetingTopic });
        resolve(true);
        return;
      }
      if (getCloudLinkStatus() !== CloudLinkStatusEnum.FREE) {
        throw new Error("会议资源重置中，请5秒后重新发起会议");
      }
      changeCloudLinkStatus(CloudLinkStatusEnum.BUSY);
      ImService.getInstance()
        .getCurrentEmployee()
        .then((userInfo: Partial<Employee>) => {
          if (!userInfo.userId) {
            changeCloudLinkStatus(CloudLinkStatusEnum.FREE);
            reject(new Error("获取当前用户失败"));
            return;
          }
          function handleCreateCb({
            status,
            error,
            data
          }: {
            status: number;
            error: string;
            data: any;
          }) {
            FocusSDK.off("cloudlink:createMeetingCb", handleCreateCb);
            // FocusSDK.off('cloudlink:joinMeetingCb', handleCreateCb);
            if (status) {
              if (isBigScreen) {
                // 声智
                fetch(
                  "https://olympics-support-platform-api-fat.soundai.net/ow/support/jb/callback?action=open"
                );
              }
              FocusSDK.on("cloudlink:callEnded", handleCallEnded);
              setCurrentCloudLinkMeetingInfo(data);
              invite({ toUsers, terminals, meetingTopic: meetingTopic });
              resolve(data);
            } else {
              changeCloudLinkStatus(CloudLinkStatusEnum.FREE);
              reject(new Error(error));
            }
          }
          // 会议结束
          function handleCallEnded() {
            if (isBigScreen) {
              fetch(
                "https://olympics-support-platform-api-fat.soundai.net/ow/support/jb/callback?action=close"
              );
            }
            FocusSDK.off("cloudlink:callEnded", handleCallEnded);
            currentMeetingInfo = null;
          }

          if (cache.cloudLinkLogin) {
            // 直接创建会议并加入
            createMeeting({
              startTime: (dayjs().valueOf() + 5000).toString(),
              duration: 2,
              topic: meetingTopic,
              password: meetingPassword,
              participantIds: [
                {
                  userId: userInfo.userId,
                  teamId: userInfo.teamId || "",
                  appId: userInfo.app || "",
                  realName: userInfo.name || "",
                  headImg: userInfo.avatar || ""
                }
              ],
              platform: MeetingPlatform.HW
            }).then(([meetingInfo, err]) => {
              if (err) {
                changeCloudLinkStatus(CloudLinkStatusEnum.FREE);
                reject(new Error(err));
                return;
              } else {
                bus.emit("cloudlink:init");
                logger.debug("直接加入", meetingInfo);
                FocusSDK.once("cloudlink:joinMeetingCb", handleCreateCb);

                setTimeout(() => {
                  FocusSDK.sendIpcMessage("cloudlink.joinMeeting", {
                    meetingInfo: {
                      password: meetingPassword,
                      meetingNumber: meetingInfo?.meetingId,
                      isvideooff: true,
                      isaudiooff: true,
                      uuid: meetingInfo?.uuid
                    }
                  });
                }, 1000);
              }
            });
          } else {
            cloudlinkRegister({
              userIds: [userInfo.userId]
            }).then(([loginInfo, err]) => {
              if (err) {
                changeCloudLinkStatus(CloudLinkStatusEnum.FREE);
                reject(new Error(err));
                return;
              }
              bus.emit("cloudlink:init");

              let topic = meetingTopic;

              FocusSDK.once("cloudlink:createMeetingCb", handleCreateCb);
              FocusSDK.sendIpcMessage("cloudlink.create", {
                meetingInfo: {
                  topic,
                  password: meetingPassword,
                  chairmanPwd: "",
                  isvideooff: isUndefined(isvideooff) ? true : isvideooff,
                  isaudiooff: isUndefined(isaudiooff) ? true : isaudiooff
                },
                loginInfo: loginInfo[0]
              });
            });
          }
        });
    } catch (error) {
      console.error(error);
      reject(error);
    }
  });
}

// 加入信创会议
export function joinCloudLink(
  userId: string,
  meetingInfo: {
    password: string;
    meetingNumber: string;
    username: string;
    isvideooff: boolean;
    isaudiooff: boolean;
  }
) {
  return new Promise((resolve, reject) => {
    if (FocusSDK.getDeviceInfo().platform !== "win32") {
      reject(new Error("信创视频会议暂只支持windows"));
      return;
    }
    if (getCloudLinkStatus() === CloudLinkStatusEnum.IN_MEETING) {
      reject(new Error("忙碌，请先结束其他会议"));
      return;
    }
    if (getCloudLinkStatus() !== CloudLinkStatusEnum.FREE) {
      reject(new Error("会议资源重置中，请稍后重试"));
      return;
    }
    changeCloudLinkStatus(CloudLinkStatusEnum.BUSY);
    setCurrentCloudLinkMeetingInfo(null);
    cloudlinkRegister({
      userIds: [userId]
    }).then(([loginInfo, err]) => {
      if (err) {
        changeCloudLinkStatus(CloudLinkStatusEnum.FREE);
        console.error(err);
        reject(new Error(err));
        changeCloudLinkStatus(CloudLinkStatusEnum.FREE);
        return;
      }
      bus.emit("cloudlink:init");
      function handleJoinCb({ status, data, error }: { status: number; data: any; error: any }) {
        FocusSDK.off("cloudlink:joinMeetingCb", handleJoinCb);
        if (status) {
          setCurrentCloudLinkMeetingInfo(data);
          resolve(data);
        } else {
          changeCloudLinkStatus(CloudLinkStatusEnum.FREE);
          reject(error);
        }
      }
      FocusSDK.on("cloudlink:joinMeetingCb", handleJoinCb);

      FocusSDK.sendIpcMessage("cloudlink.joinMeeting", {
        loginInfo: loginInfo[0],
        meetingInfo
      });
    });
  });
}

export function getDevicesInfo(type: any) {
  return new Promise((resolve, reject) => {
    function handle(param: any) {
      FocusSDK.off("cloudlink:getDevicesInfoCb", handle);
      const { data, status } = param;
      if (status === 1) {
        resolve(data);
      } else {
        reject(new Error("获取失败"));
      }
    }
    FocusSDK.on("cloudlink:getDevicesInfoCb", handle);
    FocusSDK.sendIpcMessage("cloudlink.getDevicesInfo", type);
  });
}
